import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

type CptCodeEntry = {
  code: string;
  description: string;
  price: number;
};

export type MainDrawerType =
  | "documents"
  | "medicalHistory"
  | "rxHistory"
  | "legalNotes"
  | "clinicianNotes"
  | "tasks"
  | "claimLinking"
  | "relatedFiles"
  | "email"
  | "icdCoding"
  | "reportsTable"
  | "lienPaymentHistory"
  | "lienICDCoding"
  | "backgroundJobs"
  | "clientInfo"
  | "clientFiles"
  | "clientDocuments"
  | "clientFeeds"
  | "clientExportFeeds"
  | "cptCodes";

export type ValidationDrawerType = "error" | "warning";

export type DrawerType = MainDrawerType | ValidationDrawerType;

export type DrawerState = {
  showDocumentsDrawer: boolean;
  showMedicalHistory: boolean;
  showRxHistory: boolean;
  showLegalNotes: boolean;
  showClinicianNotes: boolean;
  showTasksDrawer: boolean;
  showClaimLinkingDrawer: boolean;
  showRelatedFilesDrawer: boolean;
  showEmailDrawer: boolean;
  showICDCodingDrawer: boolean;
  showLienPaymentHistoryDrawer: boolean;
  showReportsTableDrawer: boolean;
  showLienICDCodingDrawer: boolean;
  showBackgroundJobs: boolean;
  showClientInfoDrawer: boolean;
  showClientFilesDrawer: boolean;
  showClientDocumentsDrawer: boolean;
  showClientFeedsDrawer: boolean;
  showClientExportFeedsDrawer: boolean;
  showCPTCodesDrawer: boolean;
  cptCodesDrawerTargetId: number | null;
  cptCodesDrawerInitialCodes: CptCodeEntry[];
  errorDrawerVisible: boolean;
  warningDrawerVisible: boolean;
};

// Helper type: keys of DrawerState whose values are boolean
type BooleanDrawerKey = {
  [K in keyof DrawerState]: DrawerState[K] extends boolean ? K : never;
}[keyof DrawerState];

export const drawerStateMap: Record<DrawerType, keyof DrawerState> = {
  documents: "showDocumentsDrawer",
  medicalHistory: "showMedicalHistory",
  rxHistory: "showRxHistory",
  legalNotes: "showLegalNotes",
  clinicianNotes: "showClinicianNotes",
  tasks: "showTasksDrawer",
  claimLinking: "showClaimLinkingDrawer",
  relatedFiles: "showRelatedFilesDrawer",
  email: "showEmailDrawer",
  icdCoding: "showICDCodingDrawer",
  lienPaymentHistory: "showLienPaymentHistoryDrawer",
  reportsTable: "showReportsTableDrawer",
  lienICDCoding: "showLienICDCodingDrawer",
  backgroundJobs: "showBackgroundJobs",
  clientInfo: "showClientInfoDrawer",
  clientFiles: "showClientFilesDrawer",
  clientDocuments: "showClientDocumentsDrawer",
  clientFeeds: "showClientFeedsDrawer",
  clientExportFeeds: "showClientExportFeedsDrawer",
  cptCodes: "showCPTCodesDrawer",
  error: "errorDrawerVisible",
  warning: "warningDrawerVisible",
};

/**
 * determines if a drawer is a main drawer or validation drawer
 */
export const isMainDrawer = (drawer: DrawerType): boolean => {
  return [
    "documents",
    "medicalHistory",
    "rxHistory",
    "legalNotes",
    "clinicianNotes",
    "tasks",
    "claimLinking",
    "relatedFiles",
    "email",
    "icdCoding",
    "reportsTable",
    "lienPaymentHistory",
    "lienICDCoding",
    "backgroundJobs",
    "clientInfo",
    "clientFiles",
    "clientDocuments",
    "clientFeeds",
    "clientExportFeeds",
    "cptCodes",
  ].includes(drawer);
};

/**
 * determines if a drawer is a validation drawer
 */
export const isValidationDrawer = (drawer: DrawerType): boolean => {
  return ["error", "warning"].includes(drawer);
};

/**
 * maps URL parameter values to drawer type keys for opening drawers via URL
 */
export const drawerParamToType: Record<string, MainDrawerType> = {
  documents: "documents",
  tasks: "tasks",
  medicalHistory: "medicalHistory",
  rxHistory: "rxHistory",
  legalNotes: "legalNotes",
  clinicianNotes: "clinicianNotes",
  email: "email",
  relatedFiles: "relatedFiles",
  claimLinking: "claimLinking",
  icdCoding: "icdCoding",
  lienICDCoding: "lienICDCoding",
  lienPaymentHistory: "lienPaymentHistory",
  backgroundJobs: "backgroundJobs",
  clientInfo: "clientInfo",
  clientFiles: "clientFiles",
  clientDocuments: "clientDocuments",
  clientFeeds: "clientFeeds",
  clientExportFeeds: "clientExportFeeds",
  cptCodes: "cptCodes",
};

/**
 * initial drawer state with all drawers closed
 */
const initialState: DrawerState = {
  showDocumentsDrawer: false,
  showMedicalHistory: false,
  showRxHistory: false,
  showLegalNotes: false,
  showClinicianNotes: false,
  showTasksDrawer: false,
  showClaimLinkingDrawer: false,
  showRelatedFilesDrawer: false,
  showEmailDrawer: false,
  showICDCodingDrawer: false,
  showLienPaymentHistoryDrawer: false,
  showReportsTableDrawer: false,
  showLienICDCodingDrawer: false,
  showBackgroundJobs: false,
  showClientInfoDrawer: false,
  showClientFilesDrawer: false,
  showClientDocumentsDrawer: false,
  showClientFeedsDrawer: false,
  showClientExportFeedsDrawer: false,
  showCPTCodesDrawer: false,
  cptCodesDrawerTargetId: null,
  cptCodesDrawerInitialCodes: [],
  errorDrawerVisible: false,
  warningDrawerVisible: false,
};

/**
 * closes all main content drawers without affecting validation drawers
 */
const closeMainDrawers = (state: DrawerState): void => {
  Object.entries(drawerStateMap)
    .filter(([drawer]) => isMainDrawer(drawer as DrawerType))
    .forEach(([, stateKey]) => {
      const key = stateKey as BooleanDrawerKey;
      state[key] = false;
    });
};

/**
 * closes all validation drawers (error/warning) without affecting main drawers
 */
const closeValidationDrawers = (state: DrawerState): void => {
  Object.entries(drawerStateMap)
    .filter(([drawer]) => isValidationDrawer(drawer as DrawerType))
    .forEach(([, stateKey]) => {
      const key = stateKey as BooleanDrawerKey;
      state[key] = false;
    });
};

/**
 * drawer state reducer
 * manages state for all application drawers with the following behavior:
 * - only one main drawer can be open at a time
 * - error and warning validation drawers can be open simultaneously with main drawers, but only one at a time
 */
const drawerSlice = createSlice({
  name: "drawer",
  initialState,
  reducers: {
    setShowDocumentsDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showDocumentsDrawer = action.payload;
    },
    setShowMedicalHistory: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showMedicalHistory = action.payload;
    },
    setShowRxHistory: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showRxHistory = action.payload;
    },
    setShowLegalNotes: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showLegalNotes = action.payload;
    },
    setShowClinicianNotes: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showClinicianNotes = action.payload;
    },
    setShowTasksDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showTasksDrawer = action.payload;
    },
    setShowClaimLinkingDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showClaimLinkingDrawer = action.payload;
    },
    setShowRelatedFilesDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showRelatedFilesDrawer = action.payload;
    },
    setShowEmailDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showEmailDrawer = action.payload;
    },
    setShowICDCodingDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showICDCodingDrawer = action.payload;
    },
    setShowLienICDCodingDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showLienICDCodingDrawer = action.payload;
    },
    setErrorDrawerVisible: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.warningDrawerVisible = false;
      }
      state.errorDrawerVisible = action.payload;
    },
    setWarningDrawerVisible: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        state.errorDrawerVisible = false;
      }
      state.warningDrawerVisible = action.payload;
    },
    setShowLienPaymentHistoryDrawer: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showLienPaymentHistoryDrawer = action.payload;
    },
    setShowReportsTableDrawer: (state, action: PayloadAction<boolean>) => {
      state.showReportsTableDrawer = action.payload;
    },
    setShowBackgroundJobs: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showBackgroundJobs = action.payload;
    },
    setShowClientInfoDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showClientInfoDrawer = action.payload;
    },
    setShowClientFilesDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showClientFilesDrawer = action.payload;
    },
    setShowClientDocumentsDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showClientDocumentsDrawer = action.payload;
    },
    setShowClientFeedsDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showClientFeedsDrawer = action.payload;
    },
    setShowClientExportFeedsDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showClientExportFeedsDrawer = action.payload;
    },
    setShowCPTCodesDrawer: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeMainDrawers(state);
      }
      state.showCPTCodesDrawer = action.payload;
    },
    setCPTCodesDrawerTargetId: (
      state,
      action: PayloadAction<number | null>,
    ) => {
      state.cptCodesDrawerTargetId = action.payload;
    },
    setCPTCodesDrawerInitialCodes: (
      state,
      action: PayloadAction<CptCodeEntry[]>,
    ) => {
      state.cptCodesDrawerInitialCodes = action.payload || [];
    },
    /**
     * closes all drawers, both main and validation
     * useful for actions like route changes where all drawers should be closed
     */
    closeAllDrawers: (state) => {
      closeMainDrawers(state);
      closeValidationDrawers(state);
    },

    /**
     * toggles a drawer's visibility by its type, validiation drawers are allowed to stay open
     * @param drawer the drawer type to toggle
     */
    toggleDrawer: (state, action: PayloadAction<DrawerType>) => {
      const drawerType = action.payload;
      const stateKey = drawerStateMap[drawerType] as BooleanDrawerKey;
      const isOpening = !state[stateKey];

      if (isOpening) {
        if (isMainDrawer(drawerType)) {
          closeMainDrawers(state);
        } else if (isValidationDrawer(drawerType)) {
          // close the opposing validation drawer
          if (drawerType === "error") {
            state.warningDrawerVisible = false;
          } else {
            state.errorDrawerVisible = false;
          }
        }
      }

      // toggle the drawer
      state[stateKey] = !state[stateKey];
    },
  },
});

export const {
  setShowDocumentsDrawer,
  setShowMedicalHistory,
  setShowRxHistory,
  setShowLegalNotes,
  setShowClinicianNotes,
  setShowTasksDrawer,
  setShowClaimLinkingDrawer,
  setShowRelatedFilesDrawer,
  setShowReportsTableDrawer,
  setShowEmailDrawer,
  setShowICDCodingDrawer,
  setShowLienICDCodingDrawer,
  setShowBackgroundJobs,
  setShowClientInfoDrawer,
  setShowClientFilesDrawer,
  setShowClientDocumentsDrawer,
  setShowClientFeedsDrawer,
  setShowClientExportFeedsDrawer,
  setShowCPTCodesDrawer,
  setCPTCodesDrawerTargetId,
  setCPTCodesDrawerInitialCodes,
  setErrorDrawerVisible,
  setWarningDrawerVisible,
  closeAllDrawers,
  toggleDrawer,
  setShowLienPaymentHistoryDrawer,
} = drawerSlice.actions;

export default drawerSlice.reducer;
